import React, { useEffect, useState, useRef } from 'react';
import { Question } from '../../types';
interface ColumnArithmeticProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
export function ColumnArithmetic({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: ColumnArithmeticProps) {
  const [numbers, setNumbers] = useState<number[]>([]);
  const [operation, setOperation] = useState<'+' | '-'>('+');
  const [userInputs, setUserInputs] = useState<string[]>([]);
  const [carryInputs, setCarryInputs] = useState<string[]>([]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const carryRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const cleanText = question.question.replace(/\./g, '').replace(/,/g, '.');
    const match = cleanText.match(/(\d+)\s*([+-])\s*(\d+)/);
    if (match) {
      const num1 = parseInt(match[1]);
      const op = match[2] as '+' | '-';
      const num2 = parseInt(match[3]);
      setNumbers([num1, num2]);
      setOperation(op);
      const result = op === '+' ? num1 + num2 : num1 - num2;
      const resultLength = result.toString().length;
      setUserInputs(new Array(resultLength).fill(''));
      const maxLength = Math.max(num1.toString().length, num2.toString().length);
      setCarryInputs(new Array(maxLength).fill(''));
    }
  }, [question]);
  useEffect(() => {
    const isReady = userInputs.every(v => v !== '');
    onReadyChange?.(isReady);
  }, [userInputs, onReadyChange]);
  useEffect(() => {
    if (shouldValidate) {
      // ✅ Return the user's answer as a string for distractor matching
      const userAnswer = userInputs.join('');
      onComplete(userAnswer);
    }
  }, [shouldValidate, userInputs, onComplete]);
  useEffect(() => {
    const handleFocus = () => {
      if (containerRef.current && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        setTimeout(() => {
          containerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 300);
      }
    };
    const allInputs = [...inputRefs.current, ...carryRefs.current].filter(Boolean);
    allInputs.forEach(input => {
      input?.addEventListener('focus', handleFocus);
    });
    return () => {
      allInputs.forEach(input => {
        input?.removeEventListener('focus', handleFocus);
      });
    };
  }, [userInputs.length, carryInputs.length]);
  const handleInputChange = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '');
    const newInputs = [...userInputs];
    newInputs[index] = digit;
    setUserInputs(newInputs);
    if (digit && index < userInputs.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleCarryChange = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '');
    const newCarries = [...carryInputs];
    newCarries[index] = digit;
    setCarryInputs(newCarries);
  };
  if (numbers.length !== 2) return <div data-id="element-1173">Laden...</div>;
  const [num1, num2] = numbers;
  const num1Str = num1.toString();
  const num2Str = num2.toString();
  const result = operation === '+' ? num1 + num2 : num1 - num2;
  const resultStr = result.toString();
  const gridWidth = resultStr.length;
  const paddedNum1 = num1Str.padStart(gridWidth, ' ');
  const paddedNum2 = num2Str.padStart(gridWidth, ' ');
  return <div ref={containerRef} className="flex flex-col items-center justify-center w-full max-w-md mx-auto" data-id="element-1174">

      <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-indigo-100 w-full" data-id="element-1175">
        <div className="flex justify-between items-center mb-6" data-id="element-1176">
          <h3 className="text-lg font-bold text-slate-700" data-id="element-1177">
            {operation === '+' ? 'Optellen' : 'Aftrekken'}
          </h3>
          <div className="text-sm text-slate-500 font-medium bg-slate-100 px-3 py-1 rounded-full" data-id="element-1178">
            Vul de vakjes in
          </div>
        </div>

        <div className="flex justify-center" data-id="element-1179">
          <div className="grid gap-x-2 gap-y-1" style={{
          gridTemplateColumns: `repeat(${gridWidth}, minmax(40px, 1fr))`
        }} data-id="element-1180">

            {Array.from({
            length: gridWidth
          }).map((_, i) => <div key={`carry-${i}`} className="h-8 flex items-end justify-center mb-1" data-id="element-1181">

                <input ref={el => carryRefs.current[i] = el} type="text" inputMode="numeric" className="w-6 h-6 text-center text-xs text-indigo-600 font-bold bg-indigo-50 border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400" maxLength={1} value={carryInputs[i] || ''} onChange={e => handleCarryChange(i, e.target.value)} placeholder="" data-id="element-1182" />

              </div>)}

            {paddedNum1.split('').map((digit, i) => <div key={`n1-${i}`} className="h-12 flex items-center justify-center text-2xl font-bold text-slate-800 font-mono" data-id="element-1183">

                {digit}
              </div>)}

            {paddedNum2.split('').map((digit, i) => <div key={`n2-${i}`} className="h-12 flex items-center justify-center text-2xl font-bold text-slate-800 font-mono relative" data-id="element-1184">

                {i === 0 && <span className="absolute -left-6 top-1/2 -translate-y-1/2 text-slate-400 font-sans" data-id="element-1185">
                    {operation}
                  </span>}
                {digit}
              </div>)}

            <div className="col-span-full h-0.5 bg-slate-800 my-1 rounded-full" data-id="element-1186" />

            {userInputs.map((val, i) => <div key={`input-${i}`} className="h-14 flex items-center justify-center" data-id="element-1187">

                <input ref={el => inputRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={val} onChange={e => handleInputChange(i, e.target.value)} onKeyDown={e => {
              if (e.key === 'ArrowLeft' && i > 0) {
                inputRefs.current[i - 1]?.focus();
              }
              if (e.key === 'ArrowRight' && i < userInputs.length - 1) {
                inputRefs.current[i + 1]?.focus();
              }
              if (e.key === 'Backspace' && !val && i > 0) {
                inputRefs.current[i - 1]?.focus();
              }
            }} className="w-full h-full text-center text-2xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-4 focus:ring-indigo-100 bg-white border-slate-300 text-slate-900 focus:border-indigo-500" data-id="element-1188" />

              </div>)}
          </div>
        </div>
      </div>
    </div>;
}